import { useEffect, useState } from 'react';

import { getCashinStatus, type CashinProbe, type CashinProbeState } from '@shared/services/api/cashinService';

export const CASHIN_VERIFY_FAST_INTERVAL_MS = 3000;
export const CASHIN_VERIFY_FAST_WINDOW_MS = 60000;
export const CASHIN_VERIFY_SLOW_INTERVAL_MS = 15000;

export const isFinalCashinState = (state: CashinProbeState | undefined, elapsedMs: number): boolean =>
  state === 'completed' || state === 'rejected' || (state === 'expired' && elapsedMs >= CASHIN_VERIFY_FAST_WINDOW_MS);

export interface CashinVerification {
  probe: CashinProbe | null;
  final: boolean;
  pastFastWindow: boolean;
}

const verificationOrigin = (startedAtMs: number | null | undefined): number => {
  const now = Date.now();
  if (typeof startedAtMs === 'number' && Number.isFinite(startedAtMs)) {
    return Math.min(startedAtMs, now);
  }
  return now;
};

export const useCashinVerification = (
  requestId: string | null,
  startedAtMs?: number | null
): CashinVerification => {
  const [verification, setVerification] = useState<CashinVerification>({
    probe: null,
    final: false,
    pastFastWindow: false,
  });

  useEffect(() => {
    if (!requestId) {
      setVerification({ probe: null, final: false, pastFastWindow: false });
      return undefined;
    }

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const origin = verificationOrigin(startedAtMs);
    const elapsedNow = () => Math.max(0, Date.now() - origin);
    const elapsedAtStart = elapsedNow();
    const alreadyPast = elapsedAtStart >= CASHIN_VERIFY_FAST_WINDOW_MS;

    setVerification({ probe: null, final: false, pastFastWindow: alreadyPast });

    const markWindow = () => {
      if (cancelled) {
        return;
      }
      const elapsed = elapsedNow();
      setVerification((prev) => ({
        probe: prev.probe,
        pastFastWindow: true,
        final: prev.final || isFinalCashinState(prev.probe?.state, elapsed),
      }));
    };

    const windowTimer = alreadyPast
      ? undefined
      : setTimeout(markWindow, CASHIN_VERIFY_FAST_WINDOW_MS - elapsedAtStart);

    const schedule = () => {
      timer = setTimeout(
        poll,
        elapsedNow() < CASHIN_VERIFY_FAST_WINDOW_MS ? CASHIN_VERIFY_FAST_INTERVAL_MS : CASHIN_VERIFY_SLOW_INTERVAL_MS
      );
    };

    const poll = async () => {
      try {
        const body = await getCashinStatus(requestId);
        if (cancelled) {
          return;
        }
        const probe = body?.data;
        if (!body?.errors?.length && probe?.state) {
          const elapsed = elapsedNow();
          const final = isFinalCashinState(probe.state, elapsed);
          setVerification({ probe, final, pastFastWindow: elapsed >= CASHIN_VERIFY_FAST_WINDOW_MS });
          if (final) {
            return;
          }
        }
      } catch {
        if (cancelled) {
          return;
        }
      }
      schedule();
    };

    void poll();

    return () => {
      cancelled = true;
      if (windowTimer) {
        clearTimeout(windowTimer);
      }
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [requestId, startedAtMs]);

  return verification;
};
